import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import * as Sentry from "@sentry/react";
import { useSnackbar } from "notistack";

import { useUpdateInvitationMutation } from "fond/api/invitationSlice";
import { selectUserId } from "fond/api/userSlice";
import { isBasicRtkHttpError } from "fond/types";
import { setAccountContext } from "fond/utils/accountContextStorage";
import { useAppSelector } from "fond/utils/hooks";

export function useInviteAcceptance(): {
  handleInviteAcceptance: (invitationId: string) => Promise<void>;
} {
  const [updateInvitation] = useUpdateInvitationMutation();
  const { enqueueSnackbar } = useSnackbar();
  const userId = useAppSelector(selectUserId);
  const navigate = useNavigate();
  const { state: locationState } = useLocation();

  const handleInviteAcceptance = useCallback(
    async (invitationId: string) => {
      try {
        const data = await updateInvitation({ invitation: { ID: invitationId, InvitationStatus: "accepted" }, userType: "existing" }).unwrap();
        if (userId && data.User.ID === userId) {
          enqueueSnackbar("Switching account...");
          setAccountContext(userId, data.AccountID);
        } else {
          Sentry.captureMessage("Invited user doesn't match current logged in user");
          navigate("/invitations/invalid");
          return;
        }
        const { invitationId: _, ...updatedState } = locationState;
        // use 2 navigates, first to remove the invitationId from the state and then to reload the page.
        navigate("/", { state: { ...updatedState } });
        navigate(0);
      } catch (error) {
        if (isBasicRtkHttpError(error)) {
          Sentry.captureException(error);
          if (error.data.message.includes("multiple accounts")) {
            navigate("/invitations/invalid", { state: { multipleAccountsError: true } });
            return;
          }
          navigate("/invitations/invalid");
        }
      }
    },
    [enqueueSnackbar, locationState, navigate, updateInvitation, userId]
  );

  return { handleInviteAcceptance };
}

export function useHandleInviteAcceptance(): { isUpdating: boolean } {
  const [isUpdating, setIsUpdating] = useState(false);
  const { state: locationState } = useLocation();
  const { handleInviteAcceptance } = useInviteAcceptance();

  useEffect(() => {
    if (!locationState?.invitationId) return;

    const acceptInvite = async () => {
      setIsUpdating(true);
      await handleInviteAcceptance(locationState.invitationId);
      setIsUpdating(false);
    };

    acceptInvite();
  }, [handleInviteAcceptance, locationState?.invitationId]);

  return { isUpdating };
}
